import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHandshake, faShoppingCart, faHeart, faBookmark, faBars, faTimes } from '@fortawesome/free-solid-svg-icons';
import useThemeStore from '../../store/themeStore';
import useTokenStore from '../../store/store';
import { ThemeProps } from '../../utils/theme';
import { useNavigate, useParams } from 'react-router-dom';
import FavoritesModal from './FavoritesModal';
import { toggleFavoriteProperty } from '../../utils/favourites';
import { useFavoritesStore } from '../../store/favoritesStore';

interface FloatingActionButtonsProps {
  themeColor: string;
  textColor: string;
  // Sold OR reserved: the floating Reserve / Make Offer controls are gated off
  // this so a sold, non-reserved listing cannot open the modals. Computed by the
  // parent via getPropertyCtaState so it stays consistent with the main CTAs.
  disabled: boolean;
  // Current-state labels straight from getPropertyCtaState (the single source of
  // truth) — already reflect enabled vs sold/reserved, so the component just
  // renders them and never hardcodes its own copy.
  offerLabel: string;
  reserveLabel: string;
}

const FloatingActionButtons: React.FC<FloatingActionButtonsProps> = ({ themeColor, textColor, disabled, offerLabel, reserveLabel }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isFavoritesModalOpen, setIsFavoritesModalOpen] = useState(false);
  const [, setHeartPulse] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const navigate = useNavigate();
  const { id: propertyId } = useParams();
  const getToken = useTokenStore((state) => state.token);
  const getThemes = useThemeStore((state) => state.theme as unknown as ThemeProps);
  const { favoriteIds, fetchFavorites } = useFavoritesStore();
  const isFavorite = propertyId ? favoriteIds.includes(propertyId) : false;

  useEffect(() => {
    const handleScroll = () => {
      const originalButtons = document.getElementById('original-buttons');
      if (originalButtons) {
        const rect = originalButtons.getBoundingClientRect();
        setIsVisible(rect.bottom < 0);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (getToken) {
      fetchFavorites();
    }
  }, [getToken, fetchFavorites]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleToggleFavorite = async () => {
    if (!propertyId || !getToken) return;
    
    try {
      const response = await toggleFavoriteProperty(propertyId, getToken);
      if (response.status === 200) {
        fetchFavorites(); // Refresh favorites list
        pulseHeart();
      }
    } catch (error) {
      console.error("Error toggling favorite:", error);
    }
  };

  const openModal = (modalId: string) => {
    if (!disabled) {
      const modal = document.getElementById(modalId);
      if (modal) {
        const hsOverlay = (window as any).HSOverlay;
        if (hsOverlay && typeof hsOverlay.open === 'function') {
          hsOverlay.open(modal);
        } else {
          console.error('HSOverlay not found or open method not available');
        }
      }
    }
  };

  const openFavoritesModal = () => {
    if (!getToken) {
      // Redirect to login if not authenticated
      navigate(`/login?redirectTo=${window.location.pathname}`);
      return;
    }
    setIsFavoritesModalOpen(true);
  };
  
  // Animate heart when toggling favorite
  const pulseHeart = () => {
    setHeartPulse(true);
    setTimeout(() => setHeartPulse(false), 800);
  };

  const isWhiteOrShade = (color: string) => {
    if (!color) return false;
    if (color.toLowerCase() === "white" || color === "rgba(255,255,255,1)") return true;
    if (/^#fff[a-f0-9]{0,3}$/i.test(color)) return true;

    const rgba = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+),?\s*([\d.]+)?\)/);
    if (rgba) {
      const [, r, g, b] = rgba.map(Number);
      return r >= 240 && g >= 240 && b >= 240;
    }

    return false;
  };
  
  // Function to get appropriate text and background colors
  const getFavoriteColors = () => {
    let backgroundColor = isFavorite ? 'rgb(239, 68, 68)' : themeColor; // Red if favorite, theme color if not
    let iconColor = isWhiteOrShade(backgroundColor) ? 'black' : textColor;
    
    return { backgroundColor, iconColor };
  };

  // Reserve Buttons 
  const themesWithoutReserveButton = ['properties.mypropertylisting.co.uk', 'Test Site 1', 'Test Site 2'];

  // Make offer and reserve buttons that appear only when scrolling
  const ActionButtons = (
    <div className="fixed bottom-64 right-8 flex flex-col space-y-4 z-[9997]">
      {/* Make Offer Button */}
      <div className="relative group" style={{ visibility: isVisible ? 'visible' : 'hidden', opacity: isVisible ? 1 : 0, transition: 'opacity 0.3s ease, visibility 0.3s ease' }}>
        <div
          className={`absolute inset-0 rounded-full ${!disabled ? 'animate-ping' : ''}`}
          style={{
            backgroundColor: disabled ? 'grey' : themeColor,
            opacity: 0.2,
            animationDuration: '2s',
          }}
        />
        <button
          onClick={() => {
            if (!getToken) {
              // Redirect to login if not authenticated
              navigate(`/login?redirectTo=/listing/${propertyId}`);
            } else if (!disabled) {
              openModal('hs-slide-down-animation-modal1');
            }
          }}
          className={`relative w-14 h-14 rounded-full flex items-center justify-center shadow-lg ${
            disabled ? 'cursor-not-allowed' : 'hover:scale-110 transition duration-300'
          }`}
          style={{
            backgroundColor: disabled ? 'grey' : themeColor,
            color: disabled ? '#ccc' : textColor,
            cursor: disabled ? 'not-allowed' : 'pointer',
          }}
          disabled={disabled}
        >
          <FontAwesomeIcon icon={faHandshake} size="lg" />
        </button>
        <div
          className="absolute right-full mr-2 top-1/2 transform -translate-y-1/2 px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap"
          style={{ backgroundColor: disabled ? 'grey' : themeColor, color: disabled ? '#ccc' : textColor }}
        >
          {offerLabel}
        </div>
      </div>

      {/* Reserve Button */}
      {!themesWithoutReserveButton.includes(getThemes.name) && (
        <div className="relative group" style={{ visibility: isVisible ? 'visible' : 'hidden', opacity: isVisible ? 1 : 0, transition: 'opacity 0.3s ease, visibility 0.3s ease' }}>
          <div
            className={`absolute inset-0 rounded-full ${!disabled ? 'animate-ping' : ''}`}
            style={{
              backgroundColor: disabled ? 'grey' : themeColor,
              opacity: 0.2,
              animationDuration: '2s',
            }}
          />
          <button
            onClick={() => {
              if (!getToken) {
                // Redirect to login if not authenticated
                navigate(`/login?redirectTo=/listing/${propertyId}`);
              } else if (!disabled) {
                openModal('hs-slide-down-animation-modal');
              }
            }}
            className={`relative w-14 h-14 rounded-full flex items-center justify-center shadow-lg ${
              disabled ? 'cursor-not-allowed' : 'hover:scale-110 transition duration-300'
            }`}
            style={{
              backgroundColor: disabled ? 'grey' : themeColor,
              color: disabled ? '#ccc' : textColor,
              cursor: disabled ? 'not-allowed' : 'pointer',
            }}
            disabled={disabled}
          >
            <FontAwesomeIcon icon={faShoppingCart} size="lg" />
          </button>
          <div
            className="absolute right-full mr-2 top-1/2 transform -translate-y-1/2 px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap"
            style={{ backgroundColor: disabled ? 'grey' : themeColor, color: disabled ? '#ccc' : textColor }}
          >
            {reserveLabel}
          </div>
        </div>
      )}
    </div>
  );

  // Heart button is always visible
  const HeartButton = (
    <div className="fixed bottom-48 right-8 z-[9998]">
      <div className="relative group">
        {/* Viewing favorites */}
        <div
          className="absolute inset-0 rounded-full animate-ping"
          style={{
            backgroundColor: themeColor,
            opacity: 0.2,
            animationDuration: '2s',
          }}
        />
        <button
          onClick={openFavoritesModal}
          className="relative w-14 h-14 rounded-full flex items-center justify-center shadow-lg hover:scale-110 transition duration-300"
          style={{
            backgroundColor: themeColor,
            color: isWhiteOrShade(themeColor) ? 'black' : textColor,
          }}
        >
          <FontAwesomeIcon icon={faHeart} size="lg" />
        </button>
        <div
          className="absolute right-full mr-2 top-1/2 transform -translate-y-1/2 px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap"
          style={{ backgroundColor: themeColor, color: isWhiteOrShade(themeColor) ? 'black' : textColor }}
        >
          View Favourites
        </div>
      </div>
    </div>
  );

  // Add to/remove from favorites button (only shown on property detail pages)
  const FavoriteButton = propertyId ? (
    <div className="fixed bottom-28 right-8 z-[9998]">
      <div className="relative group">
        <div
          className="absolute inset-0 rounded-full animate-ping"
          style={{
            backgroundColor: getFavoriteColors().backgroundColor,
            opacity: 0.2,
            animationDuration: '2s',
          }}
        />
        <button
          onClick={handleToggleFavorite}
          className="relative w-14 h-14 rounded-full flex items-center justify-center shadow-lg hover:scale-110 transition duration-300"
          style={{
            backgroundColor: getFavoriteColors().backgroundColor,
            color: getFavoriteColors().iconColor,
            opacity: 1,
            cursor: 'pointer',
          }}
        >
          <FontAwesomeIcon icon={faBookmark} size="lg" className={isFavorite ? 'text-white' : ''} />
        </button>
        <div
          className="absolute right-full mr-2 top-1/2 transform -translate-y-1/2 px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap"
          style={{ 
            backgroundColor: getFavoriteColors().backgroundColor, 
            color: getFavoriteColors().iconColor 
          }}
        >
          {isFavorite ? 'Remove from Favourites' : 'Add to Favourites'}
        </div>
      </div>
    </div>
  ) : null;

  // Mobile menu button
  const MobileMenuButton = (
    <div className="fixed bottom-32 right-8 z-[9997]">
      <div className="relative group">
        <button
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="relative w-14 h-14 rounded-full flex items-center justify-center shadow-lg hover:scale-110 transition duration-300"
          style={{
            backgroundColor: themeColor,
            color: isWhiteOrShade(themeColor) ? 'black' : textColor,
          }}
        >
          <FontAwesomeIcon icon={isMobileMenuOpen ? faTimes : faBars} size="lg" />
        </button>
        <div
          className="absolute right-full mr-2 top-1/2 transform -translate-y-1/2 px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap"
          style={{ backgroundColor: themeColor, color: isWhiteOrShade(themeColor) ? 'black' : textColor }}
        >
          {isMobileMenuOpen ? 'Close Menu' : 'Open Menu'}
        </div>
      </div>
    </div>
  );

  // Mobile menu container
  const MobileMenu = (
    <div 
      className={`fixed bottom-48 right-8 flex flex-col space-y-4 z-[9996] transition-all duration-300 ${
        isMobileMenuOpen ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none'
      }`}
    >
      {/* Make Offer Button */}
      <div className="relative group">
        <div
          className={`absolute inset-0 rounded-full ${!disabled ? 'animate-ping' : ''}`}
          style={{
            backgroundColor: disabled ? 'grey' : themeColor,
            opacity: 0.2,
            animationDuration: '2s',
          }}
        />
        <button
          onClick={() => {
            if (!getToken) {
              navigate(`/login?redirectTo=/listing/${propertyId}`);
            } else if (!disabled) {
              openModal('hs-slide-down-animation-modal1');
            }
          }}
          className={`relative w-14 h-14 rounded-full flex items-center justify-center shadow-lg ${
            disabled ? 'cursor-not-allowed' : 'hover:scale-110 transition duration-300'
          }`}
          style={{
            backgroundColor: disabled ? 'grey' : themeColor,
            color: disabled ? '#ccc' : textColor,
            cursor: disabled ? 'not-allowed' : 'pointer',
          }}
          disabled={disabled}
        >
          <FontAwesomeIcon icon={faHandshake} size="lg" />
        </button>
        <div
          className="absolute right-full mr-2 top-1/2 transform -translate-y-1/2 px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap"
          style={{ backgroundColor: disabled ? 'grey' : themeColor, color: disabled ? '#ccc' : textColor }}
        >
          {offerLabel}
        </div>
      </div>

      {/* Reserve Button */}
      {!themesWithoutReserveButton.includes(getThemes.name) && (
        <div className="relative group">
          <div
            className={`absolute inset-0 rounded-full ${!disabled ? 'animate-ping' : ''}`}
            style={{
              backgroundColor: disabled ? 'grey' : themeColor,
              opacity: 0.2,
              animationDuration: '2s',
            }}
          />
          <button
            onClick={() => {
              if (!getToken) {
                navigate(`/login?redirectTo=/listing/${propertyId}`);
              } else if (!disabled) {
                openModal('hs-slide-down-animation-modal');
              }
            }}
            className={`relative w-14 h-14 rounded-full flex items-center justify-center shadow-lg ${
              disabled ? 'cursor-not-allowed' : 'hover:scale-110 transition duration-300'
            }`}
            style={{
              backgroundColor: disabled ? 'grey' : themeColor,
              color: disabled ? '#ccc' : textColor,
              cursor: disabled ? 'not-allowed' : 'pointer',
            }}
            disabled={disabled}
          >
            <FontAwesomeIcon icon={faShoppingCart} size="lg" />
          </button>
          <div
            className="absolute right-full mr-2 top-1/2 transform -translate-y-1/2 px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap"
            style={{ backgroundColor: disabled ? 'grey' : themeColor, color: disabled ? '#ccc' : textColor }}
          >
            {reserveLabel}
          </div>
        </div>
      )}

      {/* Heart Button */}
      <div className="relative group">
        <div
          className="absolute inset-0 rounded-full animate-ping"
          style={{
            backgroundColor: themeColor,
            opacity: 0.2,
            animationDuration: '2s',
          }}
        />
        <button
          onClick={openFavoritesModal}
          className="relative w-14 h-14 rounded-full flex items-center justify-center shadow-lg hover:scale-110 transition duration-300"
          style={{
            backgroundColor: themeColor,
            color: isWhiteOrShade(themeColor) ? 'black' : textColor,
          }}
        >
          <FontAwesomeIcon icon={faHeart} size="lg" />
        </button>
        <div
          className="absolute right-full mr-2 top-1/2 transform -translate-y-1/2 px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap"
          style={{ backgroundColor: themeColor, color: isWhiteOrShade(themeColor) ? 'black' : textColor }}
        >
          View Favourites
        </div>
      </div>

      {/* Favorite Button */}
      {propertyId && (
        <div className="relative group">
          <div
            className="absolute inset-0 rounded-full animate-ping"
            style={{
              backgroundColor: getFavoriteColors().backgroundColor,
              opacity: 0.2,
              animationDuration: '2s',
            }}
          />
          <button
            onClick={handleToggleFavorite}
            className="relative w-14 h-14 rounded-full flex items-center justify-center shadow-lg hover:scale-110 transition duration-300"
            style={{
              backgroundColor: getFavoriteColors().backgroundColor,
              color: getFavoriteColors().iconColor,
              opacity: 1,
              cursor: 'pointer',
            }}
          >
            <FontAwesomeIcon icon={faBookmark} size="lg" className={isFavorite ? 'text-white' : ''} />
          </button>
          <div
            className="absolute right-full mr-2 top-1/2 transform -translate-y-1/2 px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap"
            style={{ 
              backgroundColor: getFavoriteColors().backgroundColor, 
              color: getFavoriteColors().iconColor 
            }}
          >
            {isFavorite ? 'Remove from Favourites' : 'Add to Favourites'}
          </div>
        </div>
      )}
    </div>
  );

  return (
    <>
      {isMobile ? (
        <>
          {MobileMenuButton}
          {MobileMenu}
        </>
      ) : (
        <>
          {HeartButton}
          {FavoriteButton}
          {ActionButtons}
        </>
      )}

      <FavoritesModal 
        isOpen={isFavoritesModalOpen} 
        onClose={() => setIsFavoritesModalOpen(false)}
        themeColor={themeColor}
        textColor={textColor}
      />
    </>
  );
};

export default FloatingActionButtons;